import React from 'react';
import Container from 'common/components/UI/Container';
import TrialDownloadButton from 'containers/Custom/TrialDownloadButton';
import {
  ComparisonWrapper,
  SectionIntro,
  TableShell,
  ComparisonTable,
  ProductHeading,
  ProductMark,
  TableNote,
  ComparisonCta,
} from './comparison.style';

import { TEAM_PRICE_FROM, TEAM_MIN_SEATS } from 'common/data/pricing';

const comparisonRows = [
  {
    label: 'Built for',
    saas: 'Central FinOps programs, procurement and management reporting',
    cloudPouch: 'AWS engineers, architects, DevOps, FinOps practitioners and consultants',
  },
  {
    label: 'Pricing',
    saas: 'Often a percentage of cloud spend, usage-based tiers, or annual and multi-year contracts',
    emphasizeSaas: true,
    cloudPouch: `Fixed price per license: $39/month or $399/year, and Team from $${TEAM_PRICE_FROM}/month for ${TEAM_MIN_SEATS} users`,
  },
  {
    label: 'Accounts & spend',
    saas: 'Price may increase with cloud spend, connected accounts or platform usage',
    cloudPouch: 'Unlimited AWS accounts per license, regardless of your AWS bill size',
  },
  {
    label: 'Vendor lock-in',
    saas: 'Multi-year contracts, persistent integrations, vendor-stored data and embedded reporting workflows can make switching difficult and expensive',
    emphasizeSaas: true,
    cloudPouch: 'No vendor lock-in. Cancel your subscription and stop using the app. There is no vendor infrastructure, persistent AWS integration or hosted dataset to unwind',
  },
  {
    label: 'Vendor visibility',
    saas: 'The vendor typically sees how much your company spends, which services generate the bill and how that spending changes over time',
    cloudPouch: 'CloudPouch has no direct access to your AWS billing posture or resources. All AWS-related requests run directly between your computer and AWS. CloudPouch servers do not take part',
  },
  {
    label: 'Data after leaving',
    saas: 'Data export, retention policies and deletion timelines depend on the vendor and contract',
    cloudPouch: 'No identifiable AWS billing or resource data remains on CloudPouch servers. Your local data stays under your control on your computer',
  },
  {
    label: 'Try before buying',
    saas: 'A demo, sales process or commercial agreement may be required',
    cloudPouch: [
      'Free, our basic plan, free forever',
      'Pro trial: 7 days of Pro, no credit card required',
    ],
  },
  {
    label: 'Onboarding',
    saas: 'Configure a hosted account, cloud connectors and cross-account access',
    cloudPouch: 'Install CloudPouch and select an existing AWS profile or SSO session',
  },
  {
    label: 'AWS access',
    saas: 'A vendor integration or cross-account role is commonly required',
    cloudPouch: 'Read-only access through the AWS profiles you already control',
  },
  {
    label: 'AWS footprint',
    saas: 'Persistent cross-account roles, connectors, agents or data pipelines may be required',
    cloudPouch: 'Nothing to deploy in AWS. CloudPouch uses read-only access from your desktop',
  },
  {
    label: 'From cost to action',
    saas: 'Dashboards and reports; optimization depth may depend on purchased modules',
    cloudPouch: 'Built-in Cost Insights identify concrete resources, recommended actions and estimated savings',
  },
  {
    label: 'Multi-account work',
    saas: 'Accounts and organizations must be connected to the hosted platform',
    cloudPouch: 'Switch between unlimited AWS profiles, even across organizations, in seconds, and run a Savings Scan on the account behind each one',
  },
  {
    label: 'Time to first value',
    saas: 'Depends on procurement, connector setup and initial data ingestion',
    cloudPouch: 'A Savings Scan runs all analyses in one pass, straight from your existing AWS profile',
  },
];

const ComparisonSection = () => (
  <ComparisonWrapper id="comparison">
    <Container>
      <SectionIntro>
        <span>Why CloudPouch</span>
        <h2>
          All the AWS cost insight. None of the <span>SaaS lock-in.</span>
        </h2>
        <p>
          CloudPouch delivers actionable AWS savings recommendations locally, with
          fixed per-license/user pricing and unlimited AWS accounts.
        </p>
      </SectionIntro>

      <TableShell>
        <ComparisonTable>
          <thead>
            <tr>
              <th scope="col">
                <span className="criteria-heading">What changes</span>
              </th>
              <th scope="col">
                <ProductHeading>
                  <strong>Traditional FinOps SaaS</strong>
                  <span>Hosted platform · variable pricing · vendor-managed data</span>
                </ProductHeading>
              </th>
              <th scope="col" className="cloudpouch-column">
                <ProductHeading>
                  <strong>
                    <ProductMark aria-hidden="true">✓</ProductMark>
                    CloudPouch
                  </strong>
                  <span>Desktop AWS FinOps · fixed price · unlimited AWS accounts</span>
                </ProductHeading>
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                <td>
                  <span className="mobile-column-label">Traditional FinOps SaaS</span>
                  {row.emphasizeSaas ? <strong>{row.saas}</strong> : row.saas}
                </td>
                <td className="cloudpouch-column">
                  <span className="mobile-column-label">CloudPouch</span>
                  <span className="cloudpouch-value">
                    {Array.isArray(row.cloudPouch)
                      ? row.cloudPouch.map((line, i) => (
                          <React.Fragment key={line}>
                            {i > 0 && <br />}
                            {line}
                          </React.Fragment>
                        ))
                      : row.cloudPouch}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </ComparisonTable>
      </TableShell>

      <TableNote>
        <p>
          SaaS products and contracts vary. This comparison describes common
          enterprise FinOps deployments; CloudPouch details reflect the plans and
          product available today.
        </p>
      </TableNote>
      <ComparisonCta>
        <p>Analyze your AWS costs locally</p>
        <TrialDownloadButton title="Start free with CloudPouch" location="comparison" />
      </ComparisonCta>
    </Container>
  </ComparisonWrapper>
);

export default ComparisonSection;
